import { useCallback, useState } from 'react';
import { ApiError, isNetworkError } from '@/lib/api/errors';
import { t } from '@/lib/i18n';

/**
 * Maps an auth failure onto the three places the canvas puts errors (1j):
 *
 *   fieldErrors → inline, under the offending input
 *   rateLimited → a block with a live countdown; the endpoint allows 10 per minute
 *   formError   → everything else, as a block without a countdown
 *
 * Offline is deliberately not handled here — it is a state, not an event, so the layout
 * shows it as a persistent banner instead.
 */

export interface AuthSubmitState {
  busy: boolean;
  /** Keyed by field name, e.g. { password: 'Email ou senha incorretos' }. */
  fieldErrors: Record<string, string>;
  formError: string | null;
  /** Seconds to wait, from Retry-After. Non-null only for a 429. */
  rateLimitedFor: number | null;
}

const EMPTY: AuthSubmitState = {
  busy: false,
  fieldErrors: {},
  formError: null,
  rateLimitedFor: null,
};

/** Which field a given failure belongs to. Anything unmapped becomes a form-level error. */
export type FieldMapper = (error: ApiError) => { field: string; message: string } | null;

export function useAuthSubmit(mapField: FieldMapper) {
  const [state, setState] = useState<AuthSubmitState>(EMPTY);

  const reset = useCallback(() => setState(EMPTY), []);

  const clearRateLimit = useCallback(
    () => setState((s) => ({ ...s, rateLimitedFor: null, formError: null })),
    [],
  );

  /** For client-side validation that should not cost a request. */
  const setFieldErrors = useCallback(
    (fieldErrors: Record<string, string>) => setState({ ...EMPTY, fieldErrors }),
    [],
  );

  const submit = useCallback(
    async (action: () => Promise<void>) => {
      setState({ ...EMPTY, busy: true });
      try {
        await action();
        setState(EMPTY);
      } catch (err) {
        if (isNetworkError(err)) {
          setState({
            ...EMPTY,
            formError: t('auth.offline'),
          });
          return;
        }

        if (err instanceof ApiError) {
          if (err.isRateLimited) {
            // The throttler sets Retry-After; 60s is the configured window if it is absent.
            setState({ ...EMPTY, rateLimitedFor: err.retryAfter ?? 60 });
            return;
          }

          const mapped = mapField(err);
          if (mapped) {
            setState({ ...EMPTY, fieldErrors: { [mapped.field]: mapped.message } });
            return;
          }

          setState({ ...EMPTY, formError: err.message });
          return;
        }

        setState({ ...EMPTY, formError: t('errors.generic') });
      }
    },
    [mapField],
  );

  return { ...state, submit, reset, clearRateLimit, setFieldErrors };
}
